/**
 * @file AgedDebtReport.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-23
 * @description Reports → Aged Debt. What each customer owes, by how overdue it
 *              is, beside the last three days they paid and how much on each.
 *
 *              The figures come from GET /api/reports/aged-debt
 *              (services/agedDebtService.js, where the rules are written down):
 *              authorised invoices less unallocated credits, aged on the due
 *              date; payments summed per DATE, so a remittance covering five
 *              invoices reads as one payment.
 *
 *              "Balance" narrows to customers owing at least a figure — the
 *              £20k question — on the server, since it decides which rows exist.
 *              The search box narrows what is already loaded, in the browser.
 *
 *              Export PDF opens the shared TableExportModal (orientation, fit,
 *              columns, preview, save/print/email), landscape by default: the
 *              PDF carries each payment's date and amount as their own columns,
 *              which is fifteen across.
 *
 * 1.1.0 (2026-10-23, Cam): "default to showing current, 6 months' figures,
 *              and then older than 6 months … right click column header
 *              filtering, and a rep filter".
 *              - Ageing: by calendar month of the due date by default (Current,
 *                this month and the five before, Older than 6 months), or the
 *                original 30/60/90 days — the Ageing choice in the filter row,
 *                kept per tab. Both come with every row and the headings come
 *                from the server (utils/agedDebtAgeing.js), so switching asks
 *                nothing and no month name is worked out here. It is a view,
 *                not a filter, so Clear Filters leaves it.
 *              - Rep: the account's rep (customers.accounts.rep_id), a column
 *                and a filter (reps on the loaded rows, plus "No rep"). In the
 *                browser, like the search box: Balance is the one filter that
 *                decides which rows load.
 *              - Right-click a header: the shared column filters, in the
 *                browser over the loaded rows (useColumnFilters with no
 *                facets). Text columns by value; money columns by whether the
 *                customer has anything in them; Last payment by month.
 *                Counts, totals and the PDF follow every filter.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './AgedDebtReport.css';
import Dropdown from './Dropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import LoadingSpinner from './LoadingSpinner';
import ContactModal from './ContactModal';
import TableExportModal from './TableExportModal';
import SearchHelp from './SearchHelp';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { showAlert } from './Alert';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import { matchesAllTerms } from '../utils/searchMatch';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import useColumnFilters from '../hooks/useColumnFilters';

const PAYMENT_SLOTS = [0, 1, 2];
const PAYMENT_LABELS = ['Last payment', '2nd last', '3rd last'];

const BALANCE_OPTIONS = [
  { value: 'all', label: 'All customers' },
  { value: '5000', label: 'Over £5,000' },
  { value: '10000', label: 'Over £10,000' },
  { value: '20000', label: 'Over £20,000' },
  { value: '50000', label: 'Over £50,000' },
  { value: 'custom', label: 'Custom amount…' }
];

const AGEING_OPTIONS = [
  { value: 'months', label: 'Months (default)' },
  { value: 'days', label: '30-60-90 days' }
];
const AGEING_WORDS = { months: 'By month of the due date', days: 'By days past the due date (30 / 60 / 90)' };

// The rep filter's value for a customer with no rep on its account.
const NO_REP = 'none';

const CLEARED_FILTERS = { search: '', balance: 'all', customMin: '', reps: [] };

// After the ageing columns, whichever ageing is shown.
const TAIL_COLUMNS = [
  { key: 'credits', label: 'Credits' },
  { key: 'total', label: 'Total' }
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 'YYYY-MM-DD' → 'Oct 2026' — a payment's month, for the Last payment filter. */
const monthOf = (day) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(day || ''));
  return m ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : null;
};

const repKey = (r) => (r.repId === null || r.repId === undefined ? NO_REP : String(r.repId));

/** The minimum balance the filters ask for, or null for "everyone". */
function minTotalOf(filters) {
  if (filters.balance === 'all') return null;
  if (filters.balance === 'custom') {
    const n = parseFloat(String(filters.customMin).replace(/[£,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return Number(filters.balance);
}

// Zero is blank on screen and in the PDF: a column of £0.00 hides the figures
// that matter.
const money = (v) => (Math.abs(v || 0) < 0.005 ? '' : formatCurrency(v));

/**
 * Right-click filters for the columns on show. Money columns have no range
 * filter in the shared menu, so they offer "has an amount" against (blank) —
 * the question "who has anything older than six months" in one tick.
 */
function columnFiltersFor(ageColumns) {
  const defs = {
    accountNo: { label: 'Acc #', value: (r) => r.accountNo },
    companyName: { label: 'Customer', value: (r) => r.companyName },
    rep: { label: 'Rep', value: (r) => r.repName || 'No rep' }
  };
  for (const c of [...ageColumns, ...TAIL_COLUMNS]) {
    if (c.key === 'total') continue;
    defs[c.key] = {
      label: c.label,
      value: (r) => (Math.abs(r[c.key] || 0) >= 0.005 ? (c.key === 'credits' ? 'Has credit' : 'Has an amount') : null)
    };
  }
  defs.lastPaid = { label: 'Last payment', value: (r) => monthOf(r.payments[0]?.date) || 'No payment on record' };
  return defs;
}

function AgedDebtReport({ user }) {
  const [filters, setFilters] = usePersistedState('agedDebt.filters', CLEARED_FILTERS);
  const [ageing, setAgeing] = usePersistedState('agedDebt.ageing', 'months');
  const [sort, setSort] = usePersistedState('agedDebt.sort', { field: 'total', direction: 'desc' });
  const [selectedContactId, setSelectedContactId] = usePersistedState('agedDebt.contactModalId', null);
  const [rows, setRows] = useState([]);
  const [asAt, setAsAt] = useState(null);
  const [columns, setColumns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const latest = useLatestRequest();

  useSlideOutButton([loading, rows.length]);

  const minTotal = minTotalOf(filters);
  // A filter stored before the Rep filter existed has no `reps`.
  const repFilter = useMemo(() => filters.reps || [], [filters.reps]);

  const fetchReport = useCallback(async () => {
    const ticket = latest.begin();
    const isLatest = () => latest.isLatest(ticket);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (minTotal !== null) params.set('minTotal', String(minTotal));
      const res = await fetch(`${API_URL}/api/reports/aged-debt?${params}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!isLatest()) return;
      if (!res.ok) throw new Error(data.error || 'Could not load the aged debt report');
      setRows(data.rows || []);
      setAsAt(data.asAt || null);
      setColumns(data.columns || null);
    } catch (e) {
      if (isLatest()) showAlert(e.message, 'error');
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [minTotal, latest]);

  // A typed custom amount settles before it is fetched.
  useEffect(() => {
    const t = setTimeout(fetchReport, filters.balance === 'custom' ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchReport, filters.balance]);

  // The ageing columns on show, named by the server ("Oct 2026" …).
  const ageColumns = useMemo(() => columns?.[ageing] || columns?.months || [], [columns, ageing]);
  const amountColumns = useMemo(() => [...ageColumns, ...TAIL_COLUMNS], [ageColumns]);
  const oldestKey = ageColumns.find((c) => c.oldest)?.key;

  // The reps on the loaded rows, by name, then "No rep" — plus any chosen rep
  // the rows no longer carry, so a choice never vanishes from its own list.
  const repOptions = useMemo(() => {
    const names = new Map();
    for (const r of rows) if (r.repId !== null && r.repId !== undefined) names.set(String(r.repId), r.repName || `Rep ${r.repId}`);
    const list = [...names.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
    for (const v of repFilter) {
      if (v !== NO_REP && !names.has(v)) list.push({ value: v, label: `Rep ${v} (none owing)` });
    }
    return [...list, { value: NO_REP, label: 'No rep' }];
  }, [rows, repFilter]);
  const repLabel = (v) => repOptions.find((o) => o.value === v)?.label || v;

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const searched = useMemo(() => {
    const reps = new Set(repFilter);
    return rows.filter((r) => (reps.size === 0 || reps.has(repKey(r)))
      && matchesAllTerms(
        [r.companyName, r.accountNo, r.repName], filters.search,
        { amounts: [r.total, ...r.payments.map((p) => p.amount)] }));
  }, [rows, filters.search, repFilter]);

  const columnDefs = useMemo(() => columnFiltersFor(ageColumns), [ageColumns]);
  const cols = useColumnFilters(searched, columnDefs, { storageKey: 'agedDebt.columnFilters' });

  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS,
    { labels: { balance: 'Balance', customMin: 'Balance', reps: 'Rep' } });
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;
  const clearFilters = () => {
    setFilters(CLEARED_FILTERS);
    cols.clearEverything();
  };

  // Another ageing shows other columns: their filters and their sort go with
  // them rather than narrowing the table by something no longer on screen.
  const changeAgeing = (next) => {
    if (next === ageing) return;
    setAgeing(next);
    const nextKeys = new Set([...(columns?.[next] || []), ...TAIL_COLUMNS].map((c) => c.key));
    const oldKeys = new Set(ageColumns.map((c) => c.key));
    const kept = Object.fromEntries(Object.entries(cols.selected)
      .filter(([k]) => !oldKeys.has(k) || nextKeys.has(k)));
    if (Object.keys(kept).length !== Object.keys(cols.selected).length) cols.replace(kept);
    if (oldKeys.has(sort.field) && !nextKeys.has(sort.field)) setSort({ field: 'total', direction: 'desc' });
  };

  const handleSort = (field) => setSort((prev) => ({
    field,
    direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
  }));
  const sortArrow = (field) => (sort.field === field ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : '');

  const visibleRows = useMemo(() => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    const valueOf = (r) => {
      if (sort.field === 'companyName') return (r.companyName || '').toLowerCase();
      if (sort.field === 'accountNo') return (r.accountNo || '').toLowerCase();
      if (sort.field === 'repName') return (r.repName || '￿').toLowerCase();
      if (sort.field === 'lastPaid') return r.payments[0]?.date || '';
      return r[sort.field] ?? 0;
    };
    // Copied first: sort() mutates, and this array is the hook's output.
    return [...cols.rows].sort((a, b) => {
      const va = valueOf(a); const vb = valueOf(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return (a.companyName || '').localeCompare(b.companyName || '');
    });
  }, [cols.rows, sort]);

  // Added in pence, so the columns' totals come to the Total total exactly.
  const totals = useMemo(() => {
    const out = {};
    for (const c of amountColumns) {
      out[c.key] = visibleRows.reduce((s, r) => s + Math.round((r[c.key] || 0) * 100), 0) / 100;
    }
    return out;
  }, [visibleRows, amountColumns]);

  const balanceLabel = filters.balance === 'custom'
    ? (minTotal !== null ? `Over ${formatCurrency(minTotal)}` : 'All customers')
    : BALANCE_OPTIONS.find((o) => o.value === filters.balance)?.label;

  const sortLabel = (field) => {
    if (field === 'accountNo') return 'Acc #';
    if (field === 'companyName') return 'Customer';
    if (field === 'repName') return 'Rep';
    if (field === 'lastPaid') return 'Last payment';
    return amountColumns.find((c) => c.key === field)?.label || field;
  };

  // Built when the export modal opens — what is on screen at that moment.
  const buildExport = () => {
    const exportColumns = [
      { key: 'accountNo', label: 'Acc #' },
      { key: 'companyName', label: 'Customer' },
      { key: 'repName', label: 'Rep' },
      ...amountColumns.map((c) => ({ key: c.key, label: c.label, numeric: true })),
      ...PAYMENT_SLOTS.flatMap((i) => [
        { key: `paid${i}Date`, label: PAYMENT_LABELS[i] },
        { key: `paid${i}Amount`, label: 'Amount', numeric: true }
      ])
    ];
    const exportRows = visibleRows.map((r) => {
      const row = { accountNo: r.accountNo || '', companyName: r.companyName || '', repName: r.repName || '' };
      for (const c of amountColumns) row[c.key] = money(r[c.key]);
      for (const i of PAYMENT_SLOTS) {
        const p = r.payments[i];
        row[`paid${i}Date`] = p ? formatDateShort(p.date) : '—';
        row[`paid${i}Amount`] = p ? formatCurrency(p.amount) : '';
      }
      return row;
    });
    const totalsRow = { companyName: `${visibleRows.length} customer${visibleRows.length === 1 ? '' : 's'}` };
    for (const c of amountColumns) totalsRow[c.key] = money(totals[c.key]);

    const criteria = [
      { label: 'As at', value: formatDateShort(asAt) },
      { label: 'Ageing', value: AGEING_WORDS[ageing] || AGEING_WORDS.months },
      { label: 'Balance', value: balanceLabel }
    ];
    if (repFilter.length) criteria.push({ label: 'Rep', value: repFilter.map(repLabel).join(', ') });
    if (filters.search) criteria.push({ label: 'Search', value: filters.search });
    // Column filters are the reason two exports of "the same" report can
    // differ, so they are named rather than folded into the row count.
    for (const [key, list] of Object.entries(cols.selected)) {
      if (!Array.isArray(list)) continue;
      criteria.push({ label: cols.definitions[key]?.label || key, value: list.join(', ') || 'none' });
    }
    criteria.push({ label: 'Sorted by', value: `${sortLabel(sort.field)} ${sort.direction === 'asc' ? '↑' : '↓'}` });

    return {
      title: 'Aged Debt',
      kicker: 'REPORT',
      filename: `aged-debt-${asAt || 'today'}`,
      columns: exportColumns,
      rows: exportRows,
      totals: [totalsRow],
      totalsLabel: 'Total',
      criteria,
      rowNoun: 'customers',
      scopeNote: `Authorised invoices less unallocated credit notes, aged by due date${ageing === 'days' ? ' in days past it' : ' by calendar month'}. `
        + 'Payments are customer receipts summed per day, most recent first.'
    };
  };

  const colCount = 3 + amountColumns.length + PAYMENT_SLOTS.length;
  const headerClass = (key, base) => [base, cols.isFiltered(key) ? 'col-filtered' : null].filter(Boolean).join(' ');

  return (
    <div className="aged-debt-report">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Aged Debt report</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              What each customer owes by age{asAt ? ` as at ${formatDateShort(asAt)}` : ''}, with the last three days they paid.
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={clearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button
                className="btn-slideout"
                onClick={() => setShowExport(true)}
                disabled={loading || visibleRows.length === 0}
                title={visibleRows.length === 0 ? 'Nothing to export' : 'Save, print or email this report as a PDF'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">ios_share</span></div>
                <span className="btn-label">Export PDF</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={fetchReport} disabled={loading}>
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>sync</span>
                </div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
          <div className="search-stats">
            <ColumnFilterNote cols={cols} header={headerFilters} onClear={clearFilters} />
            {visibleRows.length.toLocaleString('en-GB')} customer{visibleRows.length === 1 ? '' : 's'}
            {visibleRows.length > 0 && ` · ${formatCurrency(totals.total)} owed`}
          </div>
        </div>
      </div>

      <div className={`filter-row aged-debt-filters${filters.balance === 'custom' ? ' has-custom' : ''}`}>
        <div className="filter-group">
          <label>
            Search
            <SearchHelp amounts fields={['customer', 'account no.', 'rep', 'total', 'payment amounts']} />
          </label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            placeholder="Customer or account no…"
          />
        </div>
        <div className="filter-group">
          <label>Rep</label>
          <MultiSelectDropdown
            options={repOptions}
            value={repFilter}
            onChange={(vals) => setFilter('reps', vals)}
            placeholder="All reps"
            searchable
            searchPlaceholder="Search reps…"
            tickBoxes
            hideTags
            summarize={(vals) => (vals.length === 0 ? 'All reps'
              : vals.length === 1 ? repLabel(vals[0]) : `${vals.length} reps`)}
          />
        </div>
        <div className="filter-group">
          <label>Balance</label>
          <Dropdown
            value={filters.balance}
            onChange={(value) => setFilter('balance', value)}
            options={BALANCE_OPTIONS}
          />
        </div>
        {filters.balance === 'custom' && (
          <div className="filter-group">
            <label>Owing at least (£)</label>
            <input
              type="text"
              inputMode="decimal"
              value={filters.customMin}
              onChange={(e) => setFilter('customMin', e.target.value)}
              placeholder="e.g. 20000"
            />
          </div>
        )}
        <div className="filter-group">
          <label>Ageing</label>
          <Dropdown value={ageing} onChange={changeAgeing} options={AGEING_OPTIONS} />
        </div>
      </div>

      <div className="table-container">
        <table className={`data-table table-min-xl aged-debt-table${ageing === 'months' ? ' ageing-months' : ''}`}>
          <thead>
            <tr>
              <th {...cols.headerProps('accountNo')} className={headerClass('accountNo', 'sortable')}
                  onClick={() => handleSort('accountNo')}>Acc #{sortArrow('accountNo')}</th>
              <th {...cols.headerProps('companyName')} className={headerClass('companyName', 'sortable')}
                  onClick={() => handleSort('companyName')}>Customer{sortArrow('companyName')}</th>
              <th {...cols.headerProps('rep')} className={headerClass('rep', 'sortable rep-col')}
                  onClick={() => handleSort('repName')}>Rep{sortArrow('repName')}</th>
              {amountColumns.map((c) => (
                <th key={c.key} {...cols.headerProps(c.key)} className={headerClass(c.key, 'sortable num')}
                    onClick={() => handleSort(c.key)}
                    title={c.month ? `Due in ${c.label} and not yet paid` : undefined}>
                  {c.label}{sortArrow(c.key)}
                </th>
              ))}
              {PAYMENT_SLOTS.map((i) => (
                <th key={i}
                    {...(i === 0 ? cols.headerProps('lastPaid') : {})}
                    className={headerClass(i === 0 ? 'lastPaid' : null,
                      `num payment-col${i === 0 ? ' sortable first-payment' : ''}`)}
                    onClick={i === 0 ? () => handleSort('lastPaid') : undefined}
                    title={i === 0 ? 'Sort by the date of the last payment' : undefined}>
                  {PAYMENT_LABELS[i]}{i === 0 ? sortArrow('lastPaid') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={colCount}><LoadingSpinner /></td></tr>
            ) : visibleRows.length === 0 ? (
              <tr><td colSpan={colCount} className="no-results">No customers owe money on these filters.</td></tr>
            ) : visibleRows.map((r) => (
              <tr key={r.accountId}>
                <td>
                  {r.accountNo && (
                    <span className="account-badge-inline" style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedContactId(r.accountId)}>
                      {r.accountNo}
                    </span>
                  )}
                </td>
                <td>
                  <span className="contact-link" onClick={() => setSelectedContactId(r.accountId)}>
                    {r.companyName}
                  </span>
                </td>
                <td className={`rep-col${r.repName ? '' : ' rep-none'}`}>{r.repName || '—'}</td>
                {amountColumns.map((c) => (
                  <td key={c.key}
                      className={`num${c.key === 'total' ? ' total-col' : ''}${c.key === oldestKey && r[c.key] > 0 ? ' overdue-90' : ''}`}>
                    {money(r[c.key])}
                  </td>
                ))}
                {PAYMENT_SLOTS.map((i) => {
                  const p = r.payments[i];
                  return (
                    <td key={i} className={`num payment-col${i === 0 ? ' first-payment' : ''}`}
                        title={p ? `${p.count} payment${p.count === 1 ? '' : 's'} on this day` : 'No earlier payment on record'}>
                      {p ? (
                        <>
                          <span className="payment-date">{formatDateShort(p.date)}</span>
                          <span className="payment-amount">{formatCurrency(p.amount)}</span>
                        </>
                      ) : <span className="payment-none">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {visibleRows.length > 0 && (
            <tfoot>
              <tr className="grand-total">
                <td colSpan={3}>{visibleRows.length} customer{visibleRows.length === 1 ? '' : 's'}</td>
                {amountColumns.map((c) => (
                  <td key={c.key} className="num">{money(totals[c.key])}</td>
                ))}
                <td colSpan={PAYMENT_SLOTS.length} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <ColumnFilterMenu cols={cols} />

      {showExport && (
        <TableExportModal
          build={buildExport}
          user={user}
          title="Aged Debt"
          modalTitle="Export Aged Debt"
          defaultOrientation="landscape"
          onClose={() => setShowExport(false)}
          onDone={(message) => {
            setShowExport(false);
            if (message) showAlert(message, 'success');
          }}
        />
      )}

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal
          contactId={selectedContactId}
          onClose={() => { setSelectedContactId(null); fetchReport(); }}
        />,
        document.body
      )}
    </div>
  );
}

export default AgedDebtReport;
